/**
 * Nota fiscal sobre o Supabase (0176, 0177).
 *
 * Tudo por função: `emitentes_fiscais` não se lê direto (guarda os ids do
 * Vault), e a nota só se escreve pelas RPCs, que conferem permissão, teto do
 * título e pendências. O certificado, a ligação com o Focus, a emissão e o
 * cancelamento vão pela borda `nota-fiscal`: ela pede ao banco com o token da
 * pessoa e já trata a nota na hora, sem esperar o relógio.
 */

import { supabase } from '@/supabase';
import { clinicaAtiva } from './clinicaAtiva';
import { arquivoEmBase64 } from './notasFiscais';
import type {
    AlvoFiscal, CertificadoFiscal, DadosDoEmitente, EmitenteFiscal, FiltroDeNotas, NotaFiscal, PadraoNfse, SituacaoDaNota,
} from './notasFiscais';
import type { PortaDeDados } from './porta';

type MetodosDaNotaFiscal = Pick<PortaDeDados,
    | 'emitenteFiscal' | 'salvarEmitenteFiscal' | 'enviarCertificadoFiscal' | 'removerCertificadoFiscal'
    | 'notasFiscais' | 'prepararNotaDoTitulo' | 'prepararNotaDaFatura' | 'atualizarRascunhoDaNota'
    | 'descartarRascunhoDaNota' | 'solicitarEmissaoDaNota' | 'ligarEmissorFiscal' | 'cancelarNotaFiscal'
>;

type Linha = Record<string, unknown>;

function cliente() {
    if (!supabase) throw new Error('Servidor não configurado.');
    return supabase;
}

/** A clínica é a da sessão; a plataforma é `null` — o banco decide quem pode cada uma. */
function clinicaDoAlvo(alvo: AlvoFiscal): string | null {
    if (alvo === 'plataforma') return null;
    const id = clinicaAtiva();
    if (!id) throw new Error('Sessão sem clínica: entre novamente.');
    return id;
}

function erro(mensagem: string, e: { message: string; code?: string } | null): void {
    if (!e) return;
    if (e.code === '42501') throw new Error(e.message.includes('exige') ? e.message : 'Esta ação exige a permissão do financeiro.');
    /* As frases das funções já são as da tela: "CNPJ inválido.", "Falta: …". */
    if (e.code === '22023' || e.code === 'P0002') throw new Error(e.message);
    throw new Error(`${mensagem}: ${e.message}`);
}

function texto(v: unknown): string | undefined {
    return typeof v === 'string' && v ? v : undefined;
}

export function emitenteDoJson(j: Linha | null): EmitenteFiscal | null {
    if (!j) return null;
    const t = (k: string) => (typeof j[k] === 'string' ? (j[k] as string) : null);
    const c = j.certificado as Linha | null;
    const em = j.emissor as Linha | null;
    return {
        cnpj: t('cnpj'), razaoSocial: t('razaoSocial'), nomeFantasia: t('nomeFantasia'),
        inscricaoMunicipal: t('inscricaoMunicipal'), regimeTributario: t('regimeTributario') as EmitenteFiscal['regimeTributario'],
        email: t('email'), cep: t('cep'), logradouro: t('logradouro'), numero: t('numero'), complemento: t('complemento'),
        bairro: t('bairro'), municipio: t('municipio'), codigoMunicipio: t('codigoMunicipio'), uf: t('uf'),
        itemListaServico: t('itemListaServico'), codigoTributacaoMunicipio: t('codigoTributacaoMunicipio'), cnae: t('cnae'),
        aliquotaIss: j.aliquotaIss === null || j.aliquotaIss === undefined ? null : Number(j.aliquotaIss),
        issRetido: Boolean(j.issRetido), descricaoPadrao: t('descricaoPadrao'),
        serieRps: t('serieRps') ?? '1', proximoNumeroRps: Number(j.proximoNumeroRps ?? 1),
        ambiente: j.ambiente === 'producao' ? 'producao' : 'homologacao',
        emitirAoReceber: Boolean(j.emitirAoReceber),
        codigoTributacaoNacional: t('codigoTributacaoNacional'), codigoNbs: t('codigoNbs'),
        ibsCbsCst: t('ibsCbsCst'), ibsCbsClassificacao: t('ibsCbsClassificacao'),
        regimeEspecial: Number(j.regimeEspecial ?? 0),
        provedor: t('provedor'),
        emissor: em ? {
            padrao: em.padrao === 'nacional' || em.padrao === 'municipal' ? em.padrao : null,
            ligadoEm: texto(em.ligadoEm) ?? null,
            mensagem: texto(em.mensagem) ?? null,
            cancelaPorApi: typeof em.cancelaPorApi === 'boolean' ? em.cancelaPorApi : null,
            desatualizado: Boolean(em.desatualizado),
        } : null,
        certificado: c ? {
            titular: String(c.titular ?? ''),
            ...(texto(c.cnpj) ? { cnpj: String(c.cnpj) } : {}),
            validoDe: String(c.validoDe ?? ''),
            validoAte: String(c.validoAte ?? ''),
            ...(texto(c.enviadoEm) ? { enviadoEm: String(c.enviadoEm) } : {}),
        } : null,
    };
}

function enderecoDoTomador(l: Linha): string | undefined {
    const rua = [texto(l.tomador_logradouro), texto(l.tomador_numero)].filter(Boolean).join(', ');
    const cidade = [texto(l.tomador_municipio), texto(l.tomador_uf)].filter(Boolean).join('/');
    const cep = texto(l.tomador_cep);
    const partes = [
        [rua, texto(l.tomador_complemento)].filter(Boolean).join(' '),
        [texto(l.tomador_bairro), cidade].filter(Boolean).join(', '),
        cep ? `CEP ${cep.slice(0, 5)}-${cep.slice(5)}` : undefined,
    ].filter((x) => x);
    return partes.length ? partes.join(' — ') : undefined;
}

function notaDaLinha(l: Linha): NotaFiscal {
    return {
        id: String(l.id),
        origem: l.origem as AlvoFiscal,
        clinicaId: String(l.clinica_id),
        ...(texto(l.clinica_nome) ? { clinicaNome: String(l.clinica_nome) } : {}),
        ...(texto(l.titulo_id) ? { tituloId: String(l.titulo_id) } : {}),
        ...(texto(l.titulo_descricao) ? { tituloDescricao: String(l.titulo_descricao) } : {}),
        ...(texto(l.fatura_id) ? { faturaId: String(l.fatura_id) } : {}),
        ...(texto(l.fatura_competencia) ? { faturaCompetencia: String(l.fatura_competencia) } : {}),
        situacao: l.situacao as SituacaoDaNota,
        ...(texto(l.tomador_nome) ? { tomadorNome: String(l.tomador_nome) } : {}),
        ...(texto(l.tomador_documento) ? { tomadorDocumento: String(l.tomador_documento) } : {}),
        ...(texto(l.tomador_email) ? { tomadorEmail: String(l.tomador_email) } : {}),
        ...(enderecoDoTomador(l) ? { tomadorEndereco: enderecoDoTomador(l) } : {}),
        descricao: String(l.descricao ?? ''),
        ...(texto(l.item_lista_servico) ? { itemListaServico: String(l.item_lista_servico) } : {}),
        ...(texto(l.codigo_tributacao_municipio) ? { codigoTributacaoMunicipio: String(l.codigo_tributacao_municipio) } : {}),
        ...(texto(l.codigo_tributacao_nacional) ? { codigoTributacaoNacional: String(l.codigo_tributacao_nacional) } : {}),
        ...(l.aliquota_iss !== null && l.aliquota_iss !== undefined ? { aliquotaIss: Number(l.aliquota_iss) } : {}),
        issRetido: Boolean(l.iss_retido),
        valorServicosCentavos: Number(l.valor_servicos_centavos ?? 0),
        valorIssCentavos: Number(l.valor_iss_centavos ?? 0),
        competencia: String(l.competencia ?? ''),
        ...(texto(l.rps_serie) ? { rpsSerie: String(l.rps_serie) } : {}),
        ...(l.rps_numero !== null && l.rps_numero !== undefined ? { rpsNumero: Number(l.rps_numero) } : {}),
        ...(texto(l.numero) ? { numero: String(l.numero) } : {}),
        ...(texto(l.codigo_verificacao) ? { codigoVerificacao: String(l.codigo_verificacao) } : {}),
        ...(texto(l.autorizada_em) ? { autorizadaEm: String(l.autorizada_em) } : {}),
        ...(texto(l.mensagem) ? { mensagem: String(l.mensagem) } : {}),
        ...(texto(l.pdf_caminho) ? { pdfUrl: String(l.pdf_caminho) } : {}),
        ...(texto(l.xml_caminho) ? { xmlUrl: String(l.xml_caminho) } : {}),
        ...(texto(l.url_espelho) ? { espelhoUrl: String(l.url_espelho) } : {}),
        ...(texto(l.cancelamento_motivo) ? { cancelamentoMotivo: String(l.cancelamento_motivo) } : {}),
        pendencias: Array.isArray(l.pendencias) ? (l.pendencias as unknown[]).map(String) : [],
        criadoEm: String(l.criado_em ?? ''),
    };
}

async function mensagemDaBorda(e: unknown): Promise<string | undefined> {
    const contexto = (e as { context?: unknown })?.context;
    if (contexto instanceof Response) {
        try {
            const corpo = await contexto.json();
            if (typeof corpo?.erro === 'string') return corpo.erro;
        } catch {
            /* Sem corpo legível: fica a frase genérica. */
        }
    }
    return undefined;
}

export const notasFiscaisNoSupabase: MetodosDaNotaFiscal = {
    async emitenteFiscal(alvo: AlvoFiscal): Promise<EmitenteFiscal | null> {
        const { data, error } = await cliente().rpc('emitente_fiscal', { p_clinica: clinicaDoAlvo(alvo) });
        erro('Falha ao ler a configuração fiscal', error);
        return emitenteDoJson(data as Linha | null);
    },

    async salvarEmitenteFiscal(alvo: AlvoFiscal, dados: DadosDoEmitente): Promise<EmitenteFiscal> {
        const { data, error } = await cliente().rpc('salvar_emitente_fiscal', { p_clinica: clinicaDoAlvo(alvo), p_dados: dados });
        erro('Falha ao salvar a configuração fiscal', error);
        const e = emitenteDoJson(data as Linha | null);
        if (!e) throw new Error('O servidor não devolveu a configuração salva.');
        return e;
    },

    async enviarCertificadoFiscal(alvo: AlvoFiscal, arquivo: Blob, senha: string): Promise<CertificadoFiscal & { avisoDoEmissor?: string }> {
        const { data, error } = await cliente().functions.invoke('nota-fiscal/certificado', {
            body: alvo === 'plataforma'
                ? { plataforma: true, arquivoBase64: await arquivoEmBase64(arquivo), senha }
                : { clinica: clinicaDoAlvo(alvo), arquivoBase64: await arquivoEmBase64(arquivo), senha },
        });
        if (error) throw new Error((await mensagemDaBorda(error)) ?? 'Não foi possível enviar o certificado.');
        const c = (data ?? {}) as Linha;
        if (!texto(c.validoAte)) throw new Error('O servidor não confirmou o certificado.');
        return {
            titular: String(c.titular ?? ''),
            ...(texto(c.cnpj) ? { cnpj: String(c.cnpj) } : {}),
            validoDe: String(c.validoDe ?? ''),
            validoAte: String(c.validoAte),
            ...(texto(c.avisoDoEmissor) ? { avisoDoEmissor: String(c.avisoDoEmissor) } : {}),
        };
    },

    async ligarEmissorFiscal(alvo: AlvoFiscal): Promise<{ padrao: PadraoNfse }> {
        const { data, error } = await cliente().functions.invoke('nota-fiscal/emissor', {
            body: alvo === 'plataforma' ? { plataforma: true } : { clinica: clinicaDoAlvo(alvo) },
        });
        if (error) throw new Error((await mensagemDaBorda(error)) ?? 'Não foi possível ligar ao emissor.');
        const padrao = (data as Linha | null)?.padrao;
        return { padrao: padrao === 'municipal' ? 'municipal' : 'nacional' };
    },

    async removerCertificadoFiscal(alvo: AlvoFiscal): Promise<void> {
        const { error } = await cliente().rpc('remover_certificado_fiscal', { p_clinica: clinicaDoAlvo(alvo) });
        erro('Falha ao remover o certificado', error);
    },

    async notasFiscais(filtro: FiltroDeNotas): Promise<NotaFiscal[]> {
        let consulta = cliente().from('notas_fiscais_na_tela').select('*').eq('origem', filtro.origem);
        if (filtro.origem === 'clinica' || filtro.tituloId) {
            const clinica = clinicaAtiva();
            if (clinica) consulta = consulta.eq('clinica_id', clinica);
        }
        if (filtro.tituloId) consulta = consulta.eq('titulo_id', filtro.tituloId);
        if (filtro.faturaId) consulta = consulta.eq('fatura_id', filtro.faturaId);
        const { data, error } = await consulta.order('criado_em', { ascending: false }).limit(1000);
        erro('Falha ao ler as notas fiscais', error);
        return ((data ?? []) as Linha[]).map(notaDaLinha);
    },

    async prepararNotaDoTitulo(tituloId: string, valorCentavos?: number, descricao?: string): Promise<{ id: string }> {
        const { data, error } = await cliente().rpc('preparar_nota_do_titulo', {
            p_titulo: tituloId, p_valor_centavos: valorCentavos ?? null, p_descricao: descricao ?? null,
        });
        erro('Falha ao preparar a nota', error);
        return { id: String(data) };
    },

    async prepararNotaDaFatura(faturaId: string): Promise<{ id: string }> {
        const { data, error } = await cliente().rpc('preparar_nota_da_fatura', { p_fatura: faturaId });
        erro('Falha ao preparar a nota da fatura', error);
        return { id: String(data) };
    },

    async atualizarRascunhoDaNota(id: string, mudanca: { descricao?: string; valorCentavos?: number }): Promise<void> {
        const { error } = await cliente().rpc('atualizar_rascunho_da_nota', {
            p_nota: id, p_descricao: mudanca.descricao ?? null, p_valor_centavos: mudanca.valorCentavos ?? null,
        });
        erro('Falha ao atualizar a nota', error);
    },

    async descartarRascunhoDaNota(id: string): Promise<void> {
        const { error } = await cliente().rpc('descartar_rascunho_da_nota', { p_nota: id });
        erro('Falha ao descartar a nota', error);
    },

    async solicitarEmissaoDaNota(id: string): Promise<void> {
        const { error } = await cliente().functions.invoke('nota-fiscal/emitir', { body: { nota: id } });
        if (error) throw new Error((await mensagemDaBorda(error)) ?? 'Não foi possível pedir a emissão.');
    },

    async cancelarNotaFiscal(id: string, motivo: string): Promise<void> {
        const { error } = await cliente().functions.invoke('nota-fiscal/cancelar', { body: { nota: id, motivo } });
        if (error) throw new Error((await mensagemDaBorda(error)) ?? 'Não foi possível pedir o cancelamento.');
    },
};
